import {
  gaFreshFindUnlockAccountCreated,
  gaFreshFindUnlockClick,
  gaFreshFindUnlockSignedIn,
} from '../lib/gaPixel';

/**
 * The Fresh Find Fridays Unlock funnel — two events and the thing that joins
 * them (THE-746 Phase 4, PR 4.5 / THE-770).
 *
 * THE QUESTION THIS ANSWERS. "The pill was tapped N times" is not the number
 * anyone wants; the number is **how many of those taps ended in a NEW ACCOUNT**,
 * counted apart from the people who already had one and merely signed in. That
 * needs the entry to survive the whole modal round trip, so this module ARMS on
 * the click and REPORTS at the completion chokepoint.
 *
 * THE ENTRY STRING IS `fresh_find_fridays`, VERBATIM. Every other sign-in door
 * in this codebase is hyphenated (`header-pill`, `header-dropdown`), and this
 * one is not. That is deliberate (THE-746 plan rev 4, item 23): the string has
 * to match the GA4 reporting the spec was written against, so the inconsistency
 * is left standing rather than "fixed" into a value no report knows about.
 *
 * WHY sessionStorage AND NOT A MODULE VARIABLE. The completion can land on a
 * DIFFERENT PAGE LOAD than the click: a mobile Google/Apple sign-in leaves the
 * page via `signInWithRedirect` and finishes on the return leg, where every
 * in-memory value from the click is gone. Same reason — and the same shape — as
 * `stashRedirectConfirmation` in auth/authConfirmation.tsx.
 *
 * WHY IT EXPIRES. An armed click that never converts must not sit in storage
 * and then attribute some unrelated sign-in an hour later. The window below
 * bounds the claim: past it, the arm is treated as absent and the auth is
 * reported by nobody, which is the honest outcome. Storage failures (Safari
 * private mode, quota) degrade to "not armed" — this is attribution, never
 * load-bearing, and it must not be able to break the auth path it observes.
 *
 * PRIVACY. Both events are PII-free and carry no holiday identifier: `entry` is
 * the fixed string above and `account` is one of two words. This is the one
 * Phase 4 surface with analytics (THE-746 ground rule) and it stays that way.
 */

/** The sign-in entry string, verbatim per plan item 23. */
export const FRESH_FIND_UNLOCK_ENTRY = 'fresh_find_fridays';

/**
 * Did this auth create an account, or sign one in that already existed?
 *
 * Declared here rather than imported from `auth/authConfirmation`'s
 * `AuthAccount` on purpose: that module imports THIS one (it is where the
 * report is fired from), and a type import back would point the dependency in
 * both directions. The two unions are asserted mutually assignable in
 * `__tests__/freshFindUnlockFunnel.test.ts`, so they cannot drift silently.
 */
export type FreshFindAuthAccount = 'created' | 'existing';

const PENDING_KEY = 'thea:freshFindUnlockPending';

/**
 * How long an armed click stays claimable. Generous enough for the slowest real
 * path (tap Unlock → leave for the provider → come back → create an account),
 * short enough that a user who abandoned the modal and signs in from the header
 * an hour later is not counted as a Fresh Find conversion.
 */
export const FRESH_FIND_UNLOCK_MAX_AGE_MS = 30 * 60 * 1000;

interface PendingUnlock {
  entry: string;
  ts: number;
}

function readPending(): PendingUnlock | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingUnlock>;
    if (typeof parsed.ts !== 'number' || typeof parsed.entry !== 'string') return null;
    if (Date.now() - parsed.ts > FRESH_FIND_UNLOCK_MAX_AGE_MS) return null;
    return { entry: parsed.entry, ts: parsed.ts };
  } catch {
    return null;
  }
}

/** Forget any armed click. Safe to call when nothing is armed. */
export function clearFreshFindUnlockPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* private mode / quota — nothing to clear that we could have written. */
  }
}

/**
 * The user tapped Unlock. Fires `fresh_find_unlock_click` and arms the funnel
 * so the auth that follows can be attributed to this door.
 *
 * Call this BEFORE opening the sign-in modal: a synchronous completion (the
 * in-app-browser email+password path finishes without ever leaving the page)
 * would otherwise report against an arm that had not been written yet.
 */
export function noteFreshFindUnlockClick(): void {
  gaFreshFindUnlockClick({ entry: FRESH_FIND_UNLOCK_ENTRY });
  try {
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ entry: FRESH_FIND_UNLOCK_ENTRY, ts: Date.now() }),
    );
  } catch {
    /* The click event still fired; only the join is lost. */
  }
}

/**
 * An auth completed. If it began at the Unlock pill, fire the terminal event
 * for this funnel and disarm; otherwise do nothing at all.
 *
 * NEW ACCOUNTS ARE A DIFFERENT EVENT, not a parameter on a shared one. GA4
 * counts event names natively, so `fresh_find_unlock_account_created` and
 * `fresh_find_unlock_signed_in` are separable in a report with no custom
 * dimension registered and no chance of an unregistered `account` param
 * collapsing both into one undifferentiated total.
 *
 * Disarms on BOTH outcomes — the funnel describes one click, and a click that
 * has been answered is spent.
 */
export function reportFreshFindUnlockAuth(account: FreshFindAuthAccount): void {
  const pending = readPending();
  if (!pending) return;
  clearFreshFindUnlockPending();
  if (account === 'created') {
    gaFreshFindUnlockAccountCreated({ entry: pending.entry });
  } else {
    gaFreshFindUnlockSignedIn({ entry: pending.entry });
  }
}

/** Test seam: is a click currently claimable? Not used in production code. */
export function isFreshFindUnlockPending(): boolean {
  return readPending() !== null;
}
